import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { prefersReducedMotion, startViewTransition, supportsViewTransitions } from '@theme/utilities';

const PROXY_URL = '/apps/wishlist';
const DRAWER_SECTION_ID = 'wishlist-drawer-section';
const DRAWER_ITEM_SECTION_ID = 'wishlist-item-section';
const DRAWER_REFRESH_DELAYS = [0, 200, 500, 1000, 1800];
const PENDING_STORAGE_KEY = 'esotericae:wishlist:pending';
const PENDING_TTL = 60_000;

/** @type {Set<string>} */
let wishlistIds = new Set();
/** @type {Map<string, Promise<{ok: boolean, code?: string, skipped?: boolean}>>} */
const variantQueues = new Map();
let drawerRefreshQueue = Promise.resolve();

function getStateElement() {
  return /** @type {HTMLElement | null} */ (document.querySelector('[data-wishlist-state]'));
}

function getTranslations() {
  const state = getStateElement();
  return {
    add: state?.dataset.addLabel || '',
    remove: state?.dataset.removeLabel || '',
    addProduct: state?.dataset.addProductLabel || '',
    removeProduct: state?.dataset.removeProductLabel || '',
    added: state?.dataset.added || '',
    removed: state?.dataset.removed || '',
    full: state?.dataset.full || '',
    error: state?.dataset.error || '',
    cartAdded: state?.dataset.cartAdded || '',
    cartAddedAll: state?.dataset.cartAddedAll || '',
    cartError: state?.dataset.cartError || '',
  };
}

function isCustomerLoggedIn() {
  return getStateElement()?.dataset.customerLoggedIn === 'true';
}

function writePendingMutations(mutations) {
  try {
    if (!isCustomerLoggedIn() || mutations.length === 0) {
      sessionStorage.removeItem(PENDING_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(mutations));
  } catch (error) {
    console.warn('[wishlist] Pending state could not be saved', error);
  }
}

function readPendingMutations() {
  if (!isCustomerLoggedIn()) {
    writePendingMutations([]);
    return [];
  }

  try {
    const payload = sessionStorage.getItem(PENDING_STORAGE_KEY);
    if (!payload) return [];
    const parsed = JSON.parse(payload);
    if (!Array.isArray(parsed)) {
      writePendingMutations([]);
      return [];
    }

    const now = Date.now();
    const mutations = parsed.filter(
      (mutation) =>
        mutation &&
        typeof mutation.variantId === 'string' &&
        (mutation.action === 'add' || mutation.action === 'delete') &&
        Number.isFinite(mutation.timestamp) &&
        now - mutation.timestamp >= 0 &&
        now - mutation.timestamp < PENDING_TTL
    );
    if (mutations.length !== parsed.length) writePendingMutations(mutations);
    return mutations.sort((first, second) => first.timestamp - second.timestamp);
  } catch (error) {
    console.warn('[wishlist] Pending state could not be read', error);
    writePendingMutations([]);
    return [];
  }
}

function rememberPendingMutation(variantId, action, count) {
  const mutations = readPendingMutations().filter((mutation) => mutation.variantId !== variantId);
  mutations.push({
    variantId,
    action,
    count: Number.isFinite(count) ? count : null,
    timestamp: Date.now(),
  });
  writePendingMutations(mutations);
}

function forgetPendingMutation(variantId, action) {
  writePendingMutations(
    readPendingMutations().filter(
      (mutation) => mutation.variantId !== variantId || (action && mutation.action !== action)
    )
  );
}

function reconcilePendingState(serverIds) {
  const ids = new Set(serverIds);
  const pendingMutations = [];
  let count;

  for (const mutation of readPendingMutations()) {
    const isReflected = mutation.action === 'add' ? ids.has(mutation.variantId) : !ids.has(mutation.variantId);
    if (isReflected) continue;

    pendingMutations.push(mutation);
    if (mutation.action === 'add') ids.add(mutation.variantId);
    else ids.delete(mutation.variantId);
    if (Number.isFinite(mutation.count)) count = mutation.count;
  }

  writePendingMutations(pendingMutations);
  return { ids, count: Number.isFinite(count) ? count : ids.size, pendingMutations };
}

function readServerIds(root = document) {
  const payload = root.querySelector('[data-wishlist-ids]')?.textContent?.trim();
  if (!payload) return new Set();

  try {
    const ids = JSON.parse(payload);
    if (!Array.isArray(ids)) return new Set();
    return new Set(ids.map(String));
  } catch (error) {
    console.warn('[wishlist] Invalid initial state', error);
    return new Set();
  }
}

function announce(message) {
  if (!message) return;
  const regions = document.querySelectorAll('[data-wishlist-status]');
  for (const region of regions) {
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }
}

function syncCount(count = wishlistIds.size) {
  for (const badge of document.querySelectorAll('[data-wishlist-count]')) {
    badge.textContent = String(count);
    const bubble = badge.closest('[data-wishlist-bubble]');
    if (bubble) {
      bubble.classList.toggle('visually-hidden', count === 0);
      badge.classList.toggle('hidden', count === 0);
      bubble
        .closest('.header-actions__wishlist-icon')
        ?.classList.toggle('header-actions__wishlist-icon--has-wishlist', count > 0);
    } else {
      badge.classList.toggle('visually-hidden', count === 0);
    }
  }
}

function syncButtons() {
  const translations = getTranslations();
  for (const element of document.querySelectorAll('wishlist-button[data-variant-id]')) {
    if (!(element instanceof HTMLElement)) continue;
    const variantId = element.dataset.variantId || '';
    const productTitle = element.dataset.productTitle || '';
    const active = wishlistIds.has(variantId);
    const loading = variantQueues.has(variantId);
    const button = /** @type {HTMLButtonElement | null} */ (element.querySelector('.wishlist-button'));
    if (!button) continue;

    button.classList.toggle('is-active', active);
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-pressed', String(active));
    button.disabled = loading;
    button.title = active ? translations.remove : translations.add;
    button.setAttribute(
      'aria-label',
      (active ? translations.removeProduct : translations.addProduct).replace('__PRODUCT__', productTitle)
    );
    const label = element.querySelector('[data-wishlist-button-label]');
    if (label) label.textContent = active ? translations.remove : translations.add;
  }
}

function syncUi(count = wishlistIds.size) {
  syncCount(count);
  syncButtons();
  document.dispatchEvent(new CustomEvent('wishlist:change'));
}

/** Returns a copy of the current variant IDs without exposing mutable state. */
export function getWishlistIds() { return new Set(wishlistIds); }

function openWishlistDrawer() {
  const drawer = /** @type {HTMLElement & { open?: () => void } | null} */ (document.getElementById('wishlist-drawer'));
  drawer?.open?.();
}

function openVariantChoice(element) {
  const card = element.closest('product-card');
  const chooseButton = /** @type {HTMLButtonElement | null} */ (card?.querySelector('.quick-add__button--choose'));
  if (chooseButton) {
    chooseButton.click();
    return;
  }

  const productUrl = element.dataset.productUrl;
  if (productUrl) window.location.assign(productUrl);
}

async function parseResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || `HTTP ${response.status}`);
    // @ts-ignore - preserve the HTTP status for bounded gateway recovery.
    error.status = response.status;
    // @ts-ignore - stable Nexus code is attached for UI routing.
    error.code = data.code;
    // @ts-ignore - Nexus returns the authoritative count for capacity errors.
    error.count = data.count;
    throw error;
  }
  return data;
}

async function sendWishlistMutation(variantId, action) {
  const response = await fetch(`${PROXY_URL}?action=${encodeURIComponent(action)}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action, variantID: Number(variantId) }),
  });
  return parseResponse(response);
}

/** Reads persisted Shopify state without applying the local pending overlay. */
async function readPersistedWishlist() {
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('sections', DRAWER_SECTION_ID);
  url.searchParams.set('_wishlist_verify', String(Date.now()));
  const response = await fetch(url, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Wishlist verification failed (${response.status})`);
  const sections = await response.json();
  const html = sections[DRAWER_SECTION_ID];
  if (typeof html !== 'string') throw new Error('Wishlist verification section is missing');
  const root = new DOMParser().parseFromString(html, 'text/html');
  const state = root.querySelector('[data-wishlist-state]');
  if (state?.getAttribute('data-customer-logged-in') !== 'true') {
    throw new Error('Wishlist verification requires the customer session');
  }
  const payload = root.querySelector('[data-wishlist-ids]')?.textContent;
  const ids = JSON.parse(payload || 'null');
  if (!Array.isArray(ids) || !ids.every((id) => /^\d+$/.test(String(id)))) {
    throw new Error('Wishlist verification state is invalid');
  }
  return new Set(ids.map(String));
}

async function mutateWishlist(variantId, action) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return await sendWishlistMutation(variantId, action);
    } catch (error) {
      // @ts-ignore - parseResponse preserves the original gateway status.
      const transient = [502, 503, 504].includes(error?.status) || error instanceof TypeError;
      if (!transient) throw error;

      // A gateway timeout can happen after the write. Confirm Shopify before
      // repeating the same explicit add/delete action or reporting a failure.
      try {
        const ids = await readPersistedWishlist();
        if (ids.has(String(variantId)) === (action === 'add')) return { count: ids.size };
      } catch (verificationError) {
        console.warn('[wishlist] Persisted state verification failed', verificationError);
      }
      if (attempt === 1) throw error;
      await wait(800);
    }
  }
}

function getDrawerStateFromSection(html) {
  const documentFragment = new DOMParser().parseFromString(html, 'text/html');
  const nextRoot = documentFragment.querySelector('[data-wishlist-root]');
  if (!nextRoot) throw new Error('Wishlist section did not render');
  return { nextRoot, nextIds: readServerIds(nextRoot) };
}

function replaceDrawerRoot(nextRoot) {
  const currentRoot = document.querySelector('[data-wishlist-root]');
  if (!currentRoot) throw new Error('Wishlist drawer root is missing');
  currentRoot.replaceChildren(...Array.from(nextRoot.childNodes));
}

async function fetchDrawerItem(sourceElement, variantId) {
  const productUrl = sourceElement?.dataset.productUrl;
  if (!productUrl) return null;

  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('variant', variantId);
  url.searchParams.set('section_id', DRAWER_ITEM_SECTION_ID);
  url.searchParams.set('_wishlist_item', String(Date.now()));
  const response = await fetch(url, {
    cache: 'no-store',
    headers: { Accept: 'text/html' },
  });
  if (!response.ok) throw new Error(`Wishlist item rendering failed (${response.status})`);

  const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
  const nextItem = documentFragment.querySelector('[data-wishlist-item]');
  if (!(nextItem instanceof HTMLElement) || nextItem.dataset.variantId !== variantId) {
    throw new Error('Wishlist item section did not render the requested variant');
  }
  return nextItem;
}

function syncBulkCartButton() {
  const drawerRoot = document.querySelector('[data-wishlist-root]');
  const bulkButton = /** @type {HTMLButtonElement | null} */ (drawerRoot?.querySelector('[data-wishlist-add-all]'));
  if (!bulkButton) return;
  bulkButton.disabled = !drawerRoot?.querySelector('[data-wishlist-cart-add]:not(:disabled)');
}

function ensureDrawerFooter() {
  const drawerRoot = document.querySelector('[data-wishlist-root]');
  if (!drawerRoot || drawerRoot.querySelector('.wishlist-drawer__footer')) return;
  const template = drawerRoot.querySelector('[data-wishlist-footer-template]');
  if (!(template instanceof HTMLTemplateElement)) return;

  const footer = document.importNode(template.content, true);
  const status = drawerRoot.querySelector('[data-wishlist-status]');
  if (status) status.before(footer);
  else drawerRoot.append(footer);
}

function renderDrawerHeader(templateSelector) {
  const headerRegion = document.querySelector('[data-wishlist-drawer-header]');
  const template = document.querySelector(templateSelector);
  if (!(headerRegion instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

  headerRegion.replaceChildren(document.importNode(template.content, true));
}

function insertDrawerItem(nextItem) {
  const variantId = nextItem.dataset.variantId;
  if (!variantId || document.querySelector(`[data-wishlist-item][data-variant-id="${variantId}"]`)) return;

  const content = document.querySelector('.wishlist-drawer__content');
  if (!(content instanceof HTMLElement)) return;
  let items = content.querySelector('.wishlist-drawer__items');
  if (!(items instanceof HTMLElement)) {
    items = document.createElement('div');
    items.className = 'wishlist-drawer__items';
    content.replaceChildren(items);
  }

  items.append(document.importNode(nextItem, true));
  content.classList.remove('wishlist-drawer__content--empty');
  renderDrawerHeader('[data-wishlist-filled-header-template]');
  syncCount(wishlistIds.size);
  ensureDrawerFooter();
  syncBulkCartButton();
  syncButtons();
}

async function hydratePendingDrawerItems(pendingMutations) {
  for (const mutation of pendingMutations) {
    if (mutation.action !== 'add' || document.querySelector(`[data-wishlist-item][data-variant-id="${mutation.variantId}"]`)) {
      continue;
    }

    const sourceElement = Array.from(document.querySelectorAll('wishlist-button[data-product-url]')).find(
      (element) => element instanceof HTMLElement && element.dataset.variantId === mutation.variantId
    );
    if (!(sourceElement instanceof HTMLElement)) continue;

    try {
      const nextItem = await fetchDrawerItem(sourceElement, mutation.variantId);
      if (nextItem && wishlistIds.has(mutation.variantId)) insertDrawerItem(nextItem);
    } catch (error) {
      console.warn('[wishlist] Pending drawer item could not be rendered', error);
    }
  }
}

async function renderEmptyDrawer() {
  const content = document.querySelector('.wishlist-drawer__content');
  const template = document.querySelector('[data-wishlist-empty-template]');
  if (!(content instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

  const render = () => {
    const clone = document.importNode(template.content, true);
    content.replaceChildren(clone);
    content.classList.add('wishlist-drawer__content--empty');
    renderDrawerHeader('[data-wishlist-empty-header-template]');
    document.querySelector('.wishlist-drawer__footer')?.remove();
  };

  const useFallbackAnimation = !supportsViewTransitions() && !prefersReducedMotion();
  await startViewTransition(render, ['empty-wishlist-drawer']);

  if (useFallbackAnimation) {
    content.classList.add('wishlist-drawer__content--empty-reveal');
    await new Promise((resolve) => {
      const timeout = setTimeout(resolve, 500);
      content.addEventListener(
        'animationend',
        () => {
          clearTimeout(timeout);
          resolve();
        },
        { once: true }
      );
    });
    content.classList.remove('wishlist-drawer__content--empty-reveal');
  }
}

async function removeDrawerItem(variantId) {
  const items = Array.from(document.querySelectorAll('[data-wishlist-item]'));
  const item = items.find((element) => element instanceof HTMLElement && element.dataset.variantId === variantId);
  if (!item) return;

  if (items.length === 1) {
    await renderEmptyDrawer();
    return;
  }

  item.remove();
  syncBulkCartButton();
}

function wait(delay) {
  return delay > 0 ? new Promise((resolve) => setTimeout(resolve, delay)) : Promise.resolve();
}

function refreshDrawer(expectedMutation) {
  drawerRefreshQueue = drawerRefreshQueue.then(async () => {
    const delays = expectedMutation ? DRAWER_REFRESH_DELAYS : [0];

    for (const [attempt, delay] of delays.entries()) {
      await wait(delay);
      const url = new URL(window.location.href);
      url.searchParams.set('sections', DRAWER_SECTION_ID);
      url.searchParams.set('_wishlist_refresh', `${Date.now()}-${attempt}`);
      const response = await fetch(url, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Section rendering failed (${response.status})`);
      const sections = await response.json();
      const html = sections[DRAWER_SECTION_ID];
      if (typeof html !== 'string') throw new Error('Wishlist section missing from response');

      const { nextRoot, nextIds } = getDrawerStateFromSection(html);
      const mutationIsVisible =
        !expectedMutation ||
        (expectedMutation.action === 'add'
          ? nextIds.has(expectedMutation.variantId)
          : !nextIds.has(expectedMutation.variantId));
      if (!mutationIsVisible) continue;

      if (expectedMutation) forgetPendingMutation(expectedMutation.variantId, expectedMutation.action);
      const nextState = reconcilePendingState(nextIds);
      if (nextState.pendingMutations.length === 0) replaceDrawerRoot(nextRoot);
      wishlistIds = nextState.ids;
      syncUi(nextState.count);
      syncBulkCartButton();
      return;
    }

    throw new Error('Wishlist section did not reflect the latest mutation');
  });

  drawerRefreshQueue = drawerRefreshQueue.catch((error) => {
    console.warn('[wishlist] Drawer refresh failed', error);
  });
  return drawerRefreshQueue;
}

function queueVariantMutation(variantId, action, sourceElement, { silent = false, refresh = true } = {}) {
  const previous = variantQueues.get(variantId) || Promise.resolve();
  const task = previous
    .catch(() => {})
    .then(async () => {
      if (action === 'add' && wishlistIds.has(variantId)) return { ok: true, skipped: true };
      syncButtons();
      const drawerItemPromise =
        action === 'add'
          ? fetchDrawerItem(sourceElement, variantId).catch((error) => {
              console.warn('[wishlist] Immediate drawer item rendering failed', error);
              return null;
            })
          : Promise.resolve(null);
      const data = await mutateWishlist(variantId, action);
      if (action === 'add') wishlistIds.add(variantId);
      else wishlistIds.delete(variantId);
      const count = Number.isFinite(Number(data.count)) ? Number(data.count) : wishlistIds.size;
      rememberPendingMutation(variantId, action, count);
      syncUi(count);
      if (action === 'add') {
        const nextItem = await drawerItemPromise;
        if (nextItem && wishlistIds.has(variantId)) insertDrawerItem(nextItem);
      } else {
        await removeDrawerItem(variantId);
      }
      if (!silent) announce(action === 'add' ? getTranslations().added : getTranslations().removed);
      if (refresh) refreshDrawer({ variantId, action });
      return { ok: true };
    })
    .catch(async (error) => {
      // @ts-ignore - stable Nexus code is attached in parseResponse.
      const isFull = error?.code === 'wishlist_full';
      if (isFull) {
        // @ts-ignore - stable Nexus count is attached in parseResponse.
        const serverCount = Number(error?.count);
        if (Number.isFinite(serverCount)) syncCount(serverCount);
        await refreshDrawer();
      }
      if (!silent) announce(isFull ? getTranslations().full : getTranslations().error);
      console.error('[wishlist] Mutation failed', error);
      return { ok: false, code: isFull ? 'wishlist_full' : 'request_failed' };
    })
    .finally(() => {
      if (variantQueues.get(variantId) === task) variantQueues.delete(variantId);
      syncButtons();
    });

  variantQueues.set(variantId, task);
  syncButtons();
  return task;
}

/**
 * Adds each distinct variant through the native wishlist queue. Existing favourites
 * are retained; a partial failure leaves successful additions ready for a retry.
 * @param {HTMLElement[]} elements Native wishlist controls with variant and product URLs.
 * @param {AbortSignal} [signal] Stops starting new mutations after the consumer disconnects.
 */
export async function addSelectionToWishlist(elements, signal) {
  const entries = new Map(elements.map((element) => [element.dataset.variantId, element]).filter(([id]) => /^\d+$/.test(id || '')));
  const result = { added: [], existing: [], failed: [], full: false, loginRequired: false };
  if (!isCustomerLoggedIn()) {
    result.loginRequired = true;
    openWishlistDrawer();
    return result;
  }
  let lastAdded;
  for (const [variantId, element] of entries) {
    if (signal?.aborted) { result.failed.push(variantId); continue; }
    const pending = variantQueues.get(variantId);
    if (pending) await pending;
    if (signal?.aborted) { result.failed.push(variantId); continue; }
    if (wishlistIds.has(variantId)) { result.existing.push(variantId); continue; }
    const mutation = await queueVariantMutation(variantId, 'add', element, { silent: true, refresh: false });
    if (!mutation.ok) {
      result.full = mutation.code === 'wishlist_full';
      result.failed = [...entries.keys()].filter((id) => !wishlistIds.has(id));
      break;
    }
    if (mutation.skipped) result.existing.push(variantId);
    else { result.added.push(variantId); lastAdded = variantId; }
  }
  if (lastAdded) refreshDrawer({ variantId: lastAdded, action: 'add' });
  return result;
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    syncButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.dataset.requiresChoice === 'true') {
      openVariantChoice(this);
      return;
    }

    if (!isCustomerLoggedIn()) {
      openWishlistDrawer();
      return;
    }

    const variantId = this.dataset.variantId;
    if (!variantId || variantQueues.has(variantId)) return;
    queueVariantMutation(variantId, wishlistIds.has(variantId) ? 'delete' : 'add', this);
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
}

async function addItemsToCart(items, source) {
  if (!items.length) return;

  const cartDrawerComponent = document.querySelector('cart-drawer-component');
  const hadAutoOpen = cartDrawerComponent?.hasAttribute('auto-open') || false;
  cartDrawerComponent?.removeAttribute('auto-open');

  const deferredEvent = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferredEvent.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
        sections: getCartSectionIds().join(','),
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'Cart add failed');

    const root = window.Shopify?.routes?.root || '/';
    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error('Cart refresh failed');
    const cart = await cartResponse.json();
    deferredEvent.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'wishlist-drawer',
        sourceId: 'wishlist-drawer',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        sections: result.sections,
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferredEvent.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Cart add failed',
        code: 'INVALID',
      })
    );
    throw error;
  } finally {
    if (hadAutoOpen) cartDrawerComponent?.setAttribute('auto-open', '');
  }
}

async function handleSingleCartAdd(button) {
  const variantId = Number(button.dataset.variantId);
  const quantity = Number(button.dataset.quantity);
  if (!variantId || !quantity || button.disabled) return;

  button.disabled = true;
  try {
    await addItemsToCart([{ id: variantId, quantity }], button);
    await refreshDrawer();
    announce(getTranslations().cartAdded);
  } catch (error) {
    announce(getTranslations().cartError);
    console.error('[wishlist] Cart add failed', error);
    button.disabled = false;
  }
}

async function handleBulkCartAdd(button) {
  if (button.disabled) return;
  const uniqueItems = new Map();
  for (const itemButton of document.querySelectorAll('[data-wishlist-cart-add]:not(:disabled)')) {
    if (!(itemButton instanceof HTMLButtonElement)) continue;
    const id = Number(itemButton.dataset.variantId);
    const quantity = Number(itemButton.dataset.quantity);
    if (id && quantity) uniqueItems.set(id, { id, quantity });
  }
  const items = Array.from(uniqueItems.values());
  if (!items.length) return;

  button.disabled = true;
  try {
    await addItemsToCart(items, button);
    await refreshDrawer();
    announce(getTranslations().cartAddedAll);
    const wishlistDrawer = /** @type {HTMLElement & { close?: () => Promise<void> } | null} */ (
      document.getElementById('wishlist-drawer')
    );
    await wishlistDrawer?.close?.();
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const cartDrawer = /** @type {HTMLElement & { open?: () => void } | null} */ (document.getElementById('cart-drawer'));
    cartDrawer?.open?.();
  } catch (error) {
    announce(getTranslations().cartError);
    console.error('[wishlist] Bulk cart add failed', error);
    button.disabled = false;
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const singleButton = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-wishlist-cart-add]'));
  if (singleButton) {
    event.preventDefault();
    handleSingleCartAdd(singleButton);
    return;
  }

  const bulkButton = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-wishlist-add-all]'));
  if (bulkButton) {
    event.preventDefault();
    handleBulkCartAdd(bulkButton);
  }
});

document.addEventListener('theme-drawer:open', (event) => {
  if (event.target === document.getElementById('wishlist-drawer')) {
    document.querySelector('[aria-controls="wishlist-drawer"]')?.setAttribute('aria-expanded', 'true');
  }
});

document.addEventListener('theme-drawer:close', (event) => {
  if (event.target === document.getElementById('wishlist-drawer')) {
    document.querySelector('[aria-controls="wishlist-drawer"]')?.setAttribute('aria-expanded', 'false');
  }
});

const initialState = reconcilePendingState(readServerIds());
wishlistIds = initialState.ids;
syncUi(initialState.count);

const latestPendingMutation = initialState.pendingMutations[initialState.pendingMutations.length - 1];
hydratePendingDrawerItems(initialState.pendingMutations);
if (latestPendingMutation) {
  refreshDrawer({
    variantId: latestPendingMutation.variantId,
    action: latestPendingMutation.action,
  });
}
